// src/screens/payments/PaymentScreen.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Sidebar from "../../components/dashboard/Sidebar";

// Ensure Razorpay script is loaded
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PaymentScreen = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [paying, setPaying] = useState(false);
  const [toast, setToast] = useState(null);
  const [internshipDomain, setInternshipDomain] = useState(null);
  const [paymentOrders, setPaymentOrders] = useState([]);

  const INSTALLMENTS = internshipDomain?.installments || [];


  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      
      try {
        const uSnap = await getDoc(doc(db, "internshipusers", u.uid));
        if (uSnap.exists()) setUserData(uSnap.data());

        if (applicationId) {
          const appSnap = await getDoc(doc(db, "internshipapplications", applicationId));
          if (appSnap.exists()) {
            const appData = appSnap.data();
            setApplication(appData);
            
            if (appData.internshipId) {
               const domainSnap = await getDoc(doc(db, "internshipdomains", appData.internshipId));
               if (domainSnap.exists()) {
                 setInternshipDomain(domainSnap.data());
               }
            }

            // Fetch payment orders to dynamically mark installments as paid
            const pQ = query(collection(db, "internshippaymentorder"), where("applicationId", "==", applicationId));
            const pSnap = await getDocs(pQ);
            setPaymentOrders(pSnap.docs.map(d => d.data()));
            
          } else {
            showToast("Application not found", "error");
          }
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [navigate, applicationId]);

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handlePayment = async (installment) => {
    if (installment.status !== "available") return;
    setPaying(true);

    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      showToast("Razorpay SDK failed to load. Are you online?", "error");
      setPaying(false);
      return;
    }

    try {
      const taxPercent = Number(internshipDomain?.taxPercentage) || 0;
      const taxAmount = (installment.amount * taxPercent) / 100;
      const totalAmount = Math.round((installment.amount + taxAmount) * 100) / 100;

      // 1. Create order on backend
      const response = await fetch("https://ca02ap2q0j.execute-api.us-east-1.amazonaws.com/default/api/payments/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalAmount, // This is the total
          baseAmount: installment.amount,
          taxAmount: taxAmount,
          userId: user.uid,
          applicationId: applicationId,
          appName: application?.internshipTitle || "Internship",
          phase: installment.phase,
          installmentInfo: installment.name
        }),
      });

      const data = await response.json();
      if (!data.success) {
        showToast("Failed to initiate payment. Please try again.", "error");
        setPaying(false);
        return;
      }

      // 2. Open Razorpay Checkout
      const options = {
        key: "rzp_live_TkDtBbVqfilssN", // IMPORTANT: Add your Razorpay Key ID here for frontend
        amount: data.order.amount,
        currency: data.order.currency,
        name: "Quinternship",
        description: `Payment for ${application?.internshipTitle} - ${installment.name}`,
        order_id: data.order.id,
        handler: async function (response) {
          try {
            showToast("Verifying payment...", "warn");
            
            const verifyRes = await fetch("https://ca02ap2q0j.execute-api.us-east-1.amazonaws.com/default/api/payments/verify", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                applicationId: applicationId
              }),
            });
            const verifyData = await verifyRes.json();
            
            if (verifyData.success) {
              showToast("Payment successful! Redirecting...", "success");
              setTimeout(() => {
                navigate("/dashboard/my-applications");
              }, 2000);
            } else {
              showToast("Payment verification failed.", "error");
            }
          } catch (e) {
             showToast("Error verifying payment.", "error");
          }
        },
        prefill: {
          name: userData ? `${userData.firstName} ${userData.lastName}` : "",
          email: user.email,
        },
        theme: {
          color: "#7C3AED",
        },
      };

      const paymentObject = new window.Razorpay(options);
      
      paymentObject.on("payment.failed", async function (response) {
         showToast(`Payment Failed: ${response.error.description}`, "error");
         // Log failure to backend
         await fetch("https://ca02ap2q0j.execute-api.us-east-1.amazonaws.com/default/api/payments/failure", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderId: response.error.metadata.order_id,
              paymentId: response.error.metadata.payment_id,
              errorDetails: response.error.description
            }),
         });
      });

      paymentObject.open();

    } catch (error) {
      console.error(error);
      showToast("Something went wrong.", "error");
    } finally {
      setPaying(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#F5F3FF,#EDE9FE,#DDD6FE)" }}>
      <div style={{ width: 52, height: 52, borderRadius: "50%", border: "4px solid #EDE9FE", borderTopColor: "#7C3AED", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "linear-gradient(135deg,#F5F3FF 0%,#EDE9FE 50%,#F5F3FF 100%)", fontFamily: "Inter,sans-serif" }}>
      <style>{`
        @keyframes fadeInUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
        @keyframes slideIn{from{opacity:0;transform:translateX(40px);}to{opacity:1;transform:translateX(0);}}
        @keyframes spin { to{transform:rotate(360deg);} }
        .inst-card:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(109,40,217,0.15) !important; }
        .pay-main { padding: 32px 36px 48px 28px; }
        .pay-app-row { flex-direction: row; }
        @media (max-width: 768px) {
          .pay-main { padding: 72px 16px 32px 16px !important; }
          .pay-app-row { flex-direction: column !important; gap: 12px !important; }
          .pay-app-row > div:last-child { margin-left: 0 !important; }
        }
        @media (max-width: 480px) {
          .pay-main { padding: 68px 12px 28px 12px !important; }
        }
      `}</style>
      
      <Sidebar user={user} userData={userData} collapsed={collapsed} setCollapsed={setCollapsed} />

      <main className="pay-main" style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>
        
        {toast && (
          <div style={{
            position: "fixed", top: 24, right: 28, zIndex: 9999,
            background: "rgba(255,255,255,0.97)",
            border: `2px solid ${toast.type === "success" ? "#10B981" : toast.type === "warn" ? "#F59E0B" : "#EF4444"}`,
            borderRadius: 16, padding: "14px 22px", boxShadow: "0 16px 48px rgba(109,40,217,0.18)",
            fontSize: 13, fontWeight: 600, color: "#4C1D95", animation: "slideIn 0.3s ease",
          }}>
            {toast.type === "success" ? "✅ " : toast.type === "warn" ? "⏳ " : "❌ "}{toast.msg}
          </div>
        )}

        <div style={{ marginBottom: 30, animation: "fadeInUp 0.4s ease both" }}>
          <button onClick={() => navigate("/dashboard/my-applications")} style={{ background:"transparent", border:"none", color:"#7C3AED", fontSize:14, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:6, padding:0, marginBottom:20 }}>
            ← Back to Applications
          </button>
          
          <h1 style={{ margin: 0, fontSize: 28, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>Payment Setup</h1>
          <p style={{ margin: 0, fontSize: 14, color: "#8B5CF6", fontWeight: 500, marginTop: 4 }}>Complete your internship enrollment in easy installments.</p>
        </div>

        {application && (
          <div className="pay-app-row" style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", border: "1.5px solid rgba(196,181,253,0.4)", padding: "20px 24px", borderRadius: 20, marginBottom: 30, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center", animation: "fadeInUp 0.4s 0.1s ease both" }}>
            <div style={{ width: 50, height: 50, borderRadius: 16, background: "linear-gradient(135deg,#7C3AED,#A78BFA)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>💼</div>
            <div>
              <div style={{ fontSize: 12, color: "#8B5CF6", fontWeight: 600, textTransform: "uppercase" }}>Internship Course</div>
              <div style={{ fontSize: 18, color: "#4C1D95", fontWeight: 800, fontFamily: "Outfit,sans-serif" }}>{application.internshipTitle}</div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
              <div style={{ background: "#EDE9FE", padding: "8px 16px", borderRadius: 12 }}>
                 <div style={{ fontSize: 10, color: "#7C3AED", fontWeight: 600 }}>Total Fee</div>
                 <div style={{ fontSize: 16, color: "#5B21B6", fontWeight: 800 }}>₹{internshipDomain?.totalAmount || 0}</div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
          {INSTALLMENTS.map((inst, i) => {
            const isAvail = inst.status === "available";
            const isPaid = paymentOrders.some(po => po.installmentInfo === inst.name && po.status === "Success");
            
            return (
              <div key={inst.id} className="inst-card" style={{
                background: "rgba(255,255,255,0.9)", border: `1.5px solid ${isAvail ? "#A78BFA" : "rgba(196,181,253,0.3)"}`,
                borderRadius: 20, padding: 24, transition: "all 0.3s",
                boxShadow: isAvail ? "0 8px 24px rgba(124,58,237,0.15)" : "0 4px 12px rgba(0,0,0,0.03)",
                opacity: isAvail || isPaid ? 1 : 0.6,
                animation: `fadeInUp 0.4s ${0.1 * i + 0.2}s ease both`,
                display: "flex", flexDirection: "column"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                   <span style={{ background: "#F5F3FF", color: "#7C3AED", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 8 }}>{inst.phase}</span>
                   {isPaid ? (
                     <span style={{ color: "#10B981", fontSize: 12, fontWeight: 700 }}>✅ Paid</span>
                   ) : isAvail ? (
                     <span style={{ color: "#F59E0B", fontSize: 12, fontWeight: 700 }}>⏳ Pending</span>
                   ) : (
                     <span style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 700 }}>🔒 Locked</span>
                   )}
                </div>
                
                <h3 style={{ margin: "0 0 6px 0", fontSize: 18, color: "#4C1D95", fontFamily: "Outfit,sans-serif", fontWeight: 700 }}>{inst.name}</h3>
                
                <div style={{ background: "rgba(245,243,255,0.7)", borderRadius: 12, padding: "12px 16px", marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6B7280", marginBottom: 6 }}>
                    <span>Base Fee</span>
                    <span>₹{Number(inst.amount).toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6B7280", marginBottom: 8, paddingBottom: 8, borderBottom: "1px dashed rgba(196,181,253,0.5)" }}>
                    <span>Tax ({internshipDomain?.taxPercentage || 0}%)</span>
                    <span>₹{((Number(inst.amount) * (Number(internshipDomain?.taxPercentage) || 0)) / 100).toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18, color: "#5B21B6", fontWeight: 800 }}>
                    <span>Total</span>
                    <span>₹{Math.round((Number(inst.amount) + (Number(inst.amount) * (Number(internshipDomain?.taxPercentage) || 0)) / 100) * 100) / 100}</span>
                  </div>
                </div>
                
                <div style={{ marginTop: "auto" }}>
                  {isPaid ? (
                    <button disabled style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "#D1FAE5", color: "#065F46", fontSize: 14, fontWeight: 700 }}>Payment Complete</button>
                  ) : isAvail ? (
                    <button 
                      onClick={() => handlePayment(inst)}
                      disabled={paying}
                      style={{ 
                        width: "100%", padding: "12px", borderRadius: 12, border: "none", 
                        background: paying ? "#C4B5FD" : "linear-gradient(135deg,#7C3AED,#A78BFA)", 
                        color: "#fff", fontSize: 14, fontWeight: 700, cursor: paying ? "not-allowed" : "pointer",
                        boxShadow: paying ? "none" : "0 4px 14px rgba(124,58,237,0.3)",
                      }}
                    >
                      {paying ? "Processing..." : "Pay Now"}
                    </button>
                  ) : (
                    <button disabled style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "#F3F4F6", color: "#9CA3AF", fontSize: 14, fontWeight: 700, cursor: "not-allowed" }}>
                      Locked
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default PaymentScreen;
